import * as Tabs from '@wandb/weave/components/Tabs';
import React, {useMemo, useState} from 'react';

import {useMemoizedSelector} from '../../../../state/hooks';
import {useMaybeWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {SettingsContentWrapper} from '../components/SettingsContentWrapper';
import {useSectionSettingsContextMaybe} from '../SectionSettingsContextProvider';
import {
  useMediaSettingsActions,
  useMediaSettingsContext,
} from './contexts/MediaSettingsContextProvider';
import {MediaSettingsDrawerSection} from './MediaSettingsDrawerSection';
import {makeVisibleMediaTabTitlesSelector} from './mediaSettingsSelectors';
import {type SettingsLevel} from './types';
import {getMediaSettingsTabs} from './util';

export const MediaSettingsDrawerContent = ({
  header,
  description,
  settingsLevel,
}: {
  header: React.ReactNode;
  description: string;
  settingsLevel: SettingsLevel;
}) => {
  const {mediaCardTypes, hasMasks, hasBoundingBoxes, rampFlags} =
    useMediaSettingsContext();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {panelRef} = useMediaSettingsActions();
  const staticTabs = useMemo(
    () =>
      getMediaSettingsTabs(
        mediaCardTypes ?? null,
        hasMasks,
        hasBoundingBoxes,
        settingsLevel,
        rampFlags
      ),
    [mediaCardTypes, hasMasks, hasBoundingBoxes, rampFlags, settingsLevel]
  );
  const visibleTabTitles = useMemoizedSelector(
    makeVisibleMediaTabTitlesSelector,
    staticTabs,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
  const tabs = useMemo(
    () => staticTabs.filter(tab => visibleTabTitles.includes(tab.title)),
    [staticTabs, visibleTabTitles]
  );
  const [selectedTab, setSelectedTab] = useState<string | undefined>(
    tabs?.[0]?.title
  );
  return (
    <SettingsContentWrapper
      title="Media panel"
      description={description}
      header={header}>
      <Tabs.Root value={selectedTab ?? ''} onValueChange={setSelectedTab}>
        <Tabs.List>
          {tabs.map(({title}) => (
            <Tabs.Trigger key={title} data-test={title} value={title}>
              {title}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {selectedTab && (
          <Tabs.Content value={selectedTab} tabIndex={-1}>
            <div data-test="media-settings-drawer-content">
              {tabs
                .find(tab => tab.title === selectedTab)
                ?.sections.map(section => (
                  <MediaSettingsDrawerSection
                    key={section.title}
                    {...section}
                  />
                ))}
            </div>
          </Tabs.Content>
        )}
      </Tabs.Root>
    </SettingsContentWrapper>
  );
};
